import type { Room } from "colyseus.js";
import { api, clearToken } from "../net/api.js";
import { audio } from "../game/audio.js";
import {
  type WorldState,
  type Player,
  ITEMS,
  ItemId,
  RECIPES,
  type Recipe,
  Skill,
  ownsEqualOrBetter,
  levelFromXp,
  xpForNextLevel,
  isNight,
  NIGHT_START,
  NIGHT_END,
  AOI_RADIUS,
  ClientMessage,
  type QuestPayload,
  type TicketType,
} from "@ember/shared";

const METERS: { key: keyof Player; label: string; color: string }[] = [
  { key: "hp", label: "HEALTH", color: "var(--hp)" },
  { key: "hunger", label: "HUNGER", color: "var(--hunger)" },
  { key: "warmth", label: "WARMTH", color: "var(--warmth)" },
  { key: "stamina", label: "STAMINA", color: "var(--stamina)" },
];

type DockPane = "craft" | "inv" | "skills";

/** Split total xp into (level, xp into current level, xp needed for next). */
function xpProgress(xp: number, maxLevel = 30) {
  let level = 1;
  let into = xp;
  while (level < maxLevel && into >= xpForNextLevel(level)) {
    into -= xpForNextLevel(level);
    level++;
  }
  return { level, into, need: xpForNextLevel(level) };
}

export class HUD {
  private root: HTMLElement;
  private room: Room<WorldState>;
  onPlacing: (recipeId: string | null) => void = () => {};
  private placingId: string | null = null;
  private placingFoodItem: string | null = null;
  private chatOpen = false;
  private reportOpen = false;
  private lastInvSig = "";
  private lastSkillSig = "";
  private activePane: DockPane = "craft";
  private dockOpen = true;
  private me?: Player;
  private characterId = "";
  private mapCtx: CanvasRenderingContext2D | null = null;
  private frame = 0;
  private reportType: TicketType = "BUG";

  // onboarding
  private onboarding = !localStorage.getItem("ember.onboarded");
  private spawn?: { x: number; y: number };
  private coachState = "";

  setCharacterId(id: string) {
    this.characterId = id;
  }

  constructor(root: HTMLElement, room: Room<WorldState>) {
    this.root = root;
    this.room = room;
    this.build();
    this.bindDock();
    this.bindChat();
    this.bindReport();
  }

  private el<T extends HTMLElement = HTMLElement>(sel: string): T {
    return this.root.querySelector<T>(sel)!;
  }

  private build() {
    const hud = document.createElement("div");
    hud.className = "hud";
    hud.innerHTML = `
      <button class="logout" id="logout" title="Log out">⏻ Log out</button>
      <button class="mute" id="mute" title="Toggle sound">🔊</button>
      <button class="report" id="report" title="Report a bug or request a change">🐞 Report</button>
      <div class="clock"><span class="phase" id="phase">Day</span><span id="daynum">Day 1</span><span id="time">06:00</span></div>
      <canvas class="minimap" id="minimap" width="150" height="150"></canvas>
      <div class="coach" id="coach">
        <div class="coach-title">🔥 Your first night</div>
        <ol class="coach-steps" id="coachsteps"></ol>
        <a class="coach-skip" id="coachskip">skip tutorial</a>
      </div>
      <div class="meters">
        ${METERS.map(
          (m) => `<div class="meter">
          <div class="lab"><span>${m.label}</span><span id="val-${String(m.key)}"></span></div>
          <div class="bar"><span id="bar-${String(m.key)}" style="background:${m.color}"></span></div>
        </div>`,
        ).join("")}
      </div>
      <div class="dock" id="dock">
        <div class="hint">WASD / arrows to move · click to gather, hunt &amp; talk · scroll to zoom · <b>C</b>raft · <b>I</b> bag · s<b>K</b>ills · Enter to chat</div>
        <div class="dock-tabs">
          <button class="dtab active" data-pane="craft">🔨 Craft <span class="hk">C</span></button>
          <button class="dtab" data-pane="inv">🎒 Bag <span class="hk">I</span><span class="newdot" id="invdot"></span></button>
          <button class="dtab" data-pane="skills">⚔️ Skills <span class="hk">K</span></button>
        </div>
        <div class="dock-body">
          <div class="dpane active" data-pane="craft"><div class="craftbar" id="craftbar"></div></div>
          <div class="dpane" data-pane="inv"><div class="inventory" id="inventory"></div></div>
          <div class="dpane" data-pane="skills"><div class="skillpane" id="skills"></div></div>
        </div>
      </div>
      <div class="quest" id="quest">
        <div class="qtitle" id="qtitle"></div>
        <div class="qtext" id="qtext"></div>
        <div class="qstate" id="qstate"></div>
      </div>
      <div class="bloodmoon" id="bloodmoon">🌑 BLOOD MOON — the Wraith King walks</div>
      <div class="toasts" id="toasts"></div>
      <div class="chatbox">
        <div class="chatlog" id="chatlog"></div>
        <input class="chatinput" id="chatinput" maxlength="160" placeholder="say something… (Enter to send, Esc to cancel)" />
      </div>
      <div class="interact-prompt" id="interact"></div>
      <div class="place-banner" id="placebanner"></div>
      <div class="death" id="death">
        <h2>YOU HAVE FALLEN</h2>
        <p>The dark took you. Rise again at your last campfire.</p>
        <button class="btn" id="respawn">RISE AGAIN</button>
      </div>
      <div class="report-modal" id="reportmodal">
        <div class="report-card">
          <div class="report-head">Report a bug or request a change</div>
          <div class="report-types" id="reporttypes">
            <button class="rtype active" data-type="BUG">🐞 Bug</button>
            <button class="rtype" data-type="CR">💡 Change request</button>
          </div>
          <input class="report-title" id="reporttitle" maxlength="80" placeholder="Short summary" />
          <textarea class="report-body" id="reportbody" maxlength="2000" placeholder="What happened, and what did you expect? Steps to reproduce help a lot."></textarea>
          <div class="report-actions">
            <button class="btn ghost" id="reportcancel">Cancel</button>
            <button class="btn" id="reportsend">Send</button>
          </div>
        </div>
      </div>
    `;
    this.root.appendChild(hud);
    this.buildCraftbar();
    this.el<HTMLButtonElement>("#respawn").onclick = () =>
      this.room.send(ClientMessage.Respawn);
    this.el<HTMLButtonElement>("#logout").onclick = () => this.logout();
    this.el<HTMLButtonElement>("#mute").onclick = () => {
      const on = audio.toggle();
      this.el("#mute").textContent = on ? "🔊" : "🔇";
    };
    const canvas = this.el<HTMLCanvasElement>("#minimap");
    this.mapCtx = canvas.getContext("2d");
    if (!this.onboarding) this.el("#coach").style.display = "none";
    this.el("#coachskip").onclick = () => this.finishOnboarding();
  }

  // ------------------------------------------------- unified menu dock
  /** Switch the dock to a pane; re-selecting the active pane collapses/expands. */
  private setPane(pane: DockPane, toggle = false) {
    if (toggle && pane === this.activePane) {
      this.dockOpen = !this.dockOpen;
    } else {
      this.activePane = pane;
      this.dockOpen = true;
    }
    if (pane === "inv" && this.dockOpen) this.el("#invdot").classList.remove("show");
    this.el("#dock").classList.toggle("closed", !this.dockOpen);
    this.root.querySelectorAll<HTMLElement>(".dtab").forEach((t) =>
      t.classList.toggle("active", t.dataset.pane === this.activePane),
    );
    this.root.querySelectorAll<HTMLElement>(".dpane").forEach((p) =>
      p.classList.toggle("active", p.dataset.pane === this.activePane),
    );
  }

  private bindDock() {
    this.root.querySelectorAll<HTMLButtonElement>(".dtab").forEach((btn) => {
      btn.onclick = () => this.setPane(btn.dataset.pane as DockPane, true);
    });
    const hotkeys: Record<string, DockPane> = { c: "craft", i: "inv", b: "inv", k: "skills" };
    window.addEventListener("keydown", (e) => {
      if (this.isTyping || e.ctrlKey || e.metaKey || e.altKey) return;
      const pane = hotkeys[e.key.toLowerCase()];
      if (pane) this.setPane(pane, true);
    });
  }

  private finishOnboarding() {
    this.onboarding = false;
    localStorage.setItem("ember.onboarded", "1");
    const c = this.el("#coach");
    c.style.transition = "opacity 0.5s";
    c.style.opacity = "0";
    setTimeout(() => (c.style.display = "none"), 600);
  }

  private logout() {
    clearToken();
    // persist-on-leave happens server-side; then return to the title screen
    this.room.leave(true).catch(() => {});
    location.reload();
  }

  private buildCraftbar() {
    const bar = this.el("#craftbar");
    bar.innerHTML = RECIPES.map((r) => {
      // each ingredient its own element so update() can mark missing ones red
      const cost = r.inputs
        .map((i) => `<em data-i="${i.item}">${ITEMS[i.item].icon}${i.qty}</em>`)
        .join("");
      const req = r.reqSkill && r.reqLevel ? `<span class="rq">${r.reqSkill} ${r.reqLevel}</span>` : "";
      const tier = r.id.startsWith("iron_") ? " iron" : "";
      return `<div class="recipe${tier}" data-id="${r.id}">
        <span class="emoji">${r.icon}</span>
        <span class="rname">${r.name}</span>
        <span class="cost">${cost}</span>${req}
      </div>`;
    }).join("");
    bar.querySelectorAll<HTMLElement>(".recipe").forEach((el) => {
      el.onclick = () => this.onRecipeClick(el.dataset.id!);
    });
  }

  private onRecipeClick(id: string) {
    const recipe = RECIPES.find((r) => r.id === id);
    if (!recipe) return;

    // give immediate feedback when the recipe can't be made yet
    if (this.me) {
      const reason = this.craftBlockReason(recipe, this.me);
      if (reason) {
        this.toast(reason, "warn");
        return;
      }
    }

    if (recipe.outputStructure) {
      // toggle placing mode — the player then clicks the ground to place it
      this.placingId = this.placingId === id ? null : id;
      this.placingFoodItem = null;
      this.onPlacing(this.placingId);
      this.refreshPlacingUI();
    } else {
      this.room.send(ClientMessage.Craft, { recipeId: id });
    }
  }

  /** Returns a human reason the recipe can't be made now, or null if it can. */
  private craftBlockReason(r: Recipe, me: Player): string | null {
    if (r.reqSkill && r.reqLevel) {
      const xp =
        r.reqSkill === Skill.Gathering
          ? me.skills.gatheringXp
          : r.reqSkill === Skill.Crafting
            ? me.skills.craftingXp
            : me.skills.combatXp;
      if (levelFromXp(xp) < r.reqLevel) return `Requires ${r.reqSkill} level ${r.reqLevel}.`;
    }
    if (r.outputItem && ownsEqualOrBetter(me.inventory, r.outputItem))
      return `You already have an equal or better ${r.name} — gear is used automatically.`;
    for (const ing of r.inputs) {
      let have = 0;
      for (const s of me.inventory) if (s.item === ing.item) have += s.qty;
      if (have < ing.qty) {
        const def = ITEMS[ing.item as ItemId];
        return `Need ${ing.qty} ${def?.name ?? ing.item} (have ${have}).`;
      }
    }
    return null;
  }

  clearPlacing() {
    this.placingId = null;
    this.placingFoodItem = null;
    this.onPlacing(null);
    this.refreshPlacingUI();
  }

  private refreshPlacingUI() {
    this.el("#craftbar")
      .querySelectorAll<HTMLElement>(".recipe")
      .forEach((el) => el.classList.toggle("placing", el.dataset.id === this.placingId));
    const banner = this.el("#placebanner");
    const recipe = this.placingId ? RECIPES.find((r) => r.id === this.placingId) : null;
    const food = this.placingFoodItem ? ITEMS[this.placingFoodItem as ItemId] : null;
    if (recipe) {
      banner.innerHTML = `${recipe.icon} <b>Click the ground</b> to place your ${recipe.name} · <span class="cancel">right-click / Esc to cancel</span>`;
      banner.classList.add("show");
    } else if (food) {
      banner.innerHTML = `${food.icon} <b>Click the ground</b> to set ${food.name} as bait — animals will come to eat · <span class="cancel">right-click / Esc to cancel</span>`;
      banner.classList.add("show");
    } else {
      banner.classList.remove("show");
    }
  }

  private bindChat() {
    const input = this.el<HTMLInputElement>("#chatinput");
    window.addEventListener("keydown", (e) => {
      if (e.key === "Enter" && !this.chatOpen) {
        this.chatOpen = true;
        input.classList.add("open");
        input.focus();
        e.preventDefault();
      } else if (this.chatOpen && e.key === "Enter") {
        const text = input.value.trim();
        if (text) this.room.send(ClientMessage.Chat, { text });
        input.value = "";
        input.classList.remove("open");
        input.blur();
        this.chatOpen = false;
      } else if (this.chatOpen && e.key === "Escape") {
        input.value = "";
        input.classList.remove("open");
        input.blur();
        this.chatOpen = false;
      }
    });
  }

  get isChatOpen() {
    return this.chatOpen;
  }

  /** True while a HUD text field has focus (chat or report). The scene uses
   *  this to release the keyboard so letters reach the input instead of being
   *  captured as WASD movement. */
  get isTyping() {
    return this.chatOpen || this.reportOpen;
  }
  get placingRecipe() {
    return this.placingId;
  }
  get placingFood() {
    return this.placingFoodItem;
  }

  // ---------------------------------------------------- per-frame update
  update(me: Player, state: WorldState) {
    this.me = me;
    // meters
    for (const m of METERS) {
      const v = me[m.key] as number;
      this.el(`#bar-${String(m.key)}`).style.width = `${v}%`;
      this.el(`#val-${String(m.key)}`).textContent = String(Math.round(v));
    }

    // clock
    const t = state.timeOfDay;
    const totalMin = Math.floor(t * 24 * 60);
    const hh = String(Math.floor(totalMin / 60)).padStart(2, "0");
    const mm = String(totalMin % 60).padStart(2, "0");
    this.el("#time").textContent = `${hh}:${mm}`;
    this.el("#daynum").textContent = `Day ${state.dayCount}`;
    const phaseEl = this.el("#phase");
    if (isNight(t)) phaseEl.textContent = "🌙 Night";
    else if (t >= NIGHT_START - 0.06 && t < NIGHT_START) phaseEl.textContent = "🌆 Dusk";
    else if (t >= NIGHT_END && t < NIGHT_END + 0.06) phaseEl.textContent = "🌅 Dawn";
    else phaseEl.textContent = "☀️ Day";

    // skills (only re-render when xp changed)
    const sk = [
      { name: "Gathering", icon: "🌿", xp: me.skills.gatheringXp },
      { name: "Crafting", icon: "🔨", xp: me.skills.craftingXp },
      { name: "Combat", icon: "⚔️", xp: me.skills.combatXp },
    ];
    const skillSig = sk.map((s) => s.xp).join(",");
    if (skillSig !== this.lastSkillSig) {
      this.lastSkillSig = skillSig;
      this.el("#skills").innerHTML = sk
        .map((s) => {
          const p = xpProgress(s.xp);
          const pct = Math.min(100, Math.round((p.into / p.need) * 100));
          return `<div class="srow">
            <span class="sname">${s.icon} ${s.name}</span>
            <div class="sbar"><span style="width:${pct}%"></span></div>
            <span class="slv">Lv ${p.level} <em>${p.into}/${p.need}</em></span>
          </div>`;
        })
        .join("");
    }

    // inventory (only re-render when changed)
    const sig = me.inventory.map((s) => `${s.item}:${s.qty}`).join(",");
    if (sig !== this.lastInvSig) {
      const wasEmpty = this.lastInvSig === "";
      this.lastInvSig = sig;
      this.renderInventory(me);
      // nudge the Bag tab when items change while it isn't showing
      if (!wasEmpty && (this.activePane !== "inv" || !this.dockOpen))
        this.el("#invdot").classList.add("show");
    }

    // recipe affordability + lock state, with per-ingredient have/missing marks
    const counts = new Map<string, number>();
    for (const s of me.inventory) counts.set(s.item, (counts.get(s.item) ?? 0) + s.qty);
    this.el("#craftbar")
      .querySelectorAll<HTMLElement>(".recipe")
      .forEach((el) => {
        const r = RECIPES.find((x) => x.id === el.dataset.id);
        if (!r) return;
        const owned = !!r.outputItem && ownsEqualOrBetter(me.inventory, r.outputItem);
        el.classList.toggle("owned", owned);
        el.classList.toggle("locked", owned || !this.canCraft(r, me));
        el.title = owned ? "You already have this (or a better one) — gear is used automatically." : "";
        el.querySelectorAll<HTMLElement>("em[data-i]").forEach((em) => {
          const need = r.inputs.find((i) => i.item === em.dataset.i)?.qty ?? 0;
          em.classList.toggle("miss", (counts.get(em.dataset.i!) ?? 0) < need);
        });
        const rq = el.querySelector<HTMLElement>(".rq");
        if (rq && r.reqSkill && r.reqLevel) {
          const xp =
            r.reqSkill === Skill.Gathering
              ? me.skills.gatheringXp
              : r.reqSkill === Skill.Crafting
                ? me.skills.craftingXp
                : me.skills.combatXp;
          rq.classList.toggle("bad", levelFromXp(xp) < r.reqLevel);
        }
      });

    // death overlay
    this.el("#death").classList.toggle("show", me.dead);

    this.frame++;
    if (this.onboarding) this.updateCoach(me, state);
    if (this.frame % 4 === 0) this.drawMinimap(me, state);
  }

  // ------------------------------------------------------ onboarding coach
  private updateCoach(me: Player, state: WorldState) {
    if (!this.spawn) this.spawn = { x: me.x, y: me.y };
    let built = false;
    state.structures.forEach((s) => {
      if (s.ownerId === this.characterId) built = true;
    });
    const steps = [
      { label: "Move with WASD or the arrow keys", done: Math.hypot(me.x - this.spawn.x, me.y - this.spawn.y) > 2 },
      { label: "Click a tree or rock to gather", done: me.skills.gatheringXp > 0 },
      { label: "Pick a recipe, then click the ground to build a campfire", done: built },
      { label: "Find the Elder ✦ and click to talk", done: me.questState !== "" },
    ];
    const sig = steps.map((s) => (s.done ? "1" : "0")).join("");
    if (sig !== this.coachState) {
      this.coachState = sig;
      this.el("#coachsteps").innerHTML = steps
        .map(
          (s) =>
            `<li class="${s.done ? "done" : ""}">${s.done ? "✓" : "○"} ${s.label}</li>`,
        )
        .join("");
    }
    if (steps.every((s) => s.done)) {
      this.toast("Tutorial complete — survive, and keep the fire alive. 🔥", "good");
      this.finishOnboarding();
    }
  }

  // ------------------------------------------------------------- minimap
  private drawMinimap(me: Player, state: WorldState) {
    const ctx = this.mapCtx;
    if (!ctx) return;
    const S = 150;
    const c = S / 2;
    const scale = (S / 2 - 6) / AOI_RADIUS;

    ctx.clearRect(0, 0, S, S);
    ctx.save();
    ctx.beginPath();
    ctx.arc(c, c, c - 2, 0, Math.PI * 2);
    ctx.clip();
    ctx.fillStyle = "#0c0a14";
    ctx.fillRect(0, 0, S, S);

    const plot = (ex: number, ey: number, color: string, r: number) => {
      const x = c + (ex - me.x) * scale;
      const y = c + (ey - me.y) * scale;
      ctx.fillStyle = color;
      ctx.beginPath();
      ctx.arc(x, y, r, 0, Math.PI * 2);
      ctx.fill();
    };

    state.resources.forEach((res) => {
      const col =
        res.kind === "rock"
          ? "#8a8d77"
          : res.kind === "bush"
            ? "#c86b9e"
            : res.kind === "grass"
              ? "#c9b458"
              : "#3f6f3a";
      plot(res.x, res.y, col, 1.3);
    });
    state.structures.forEach((s) => plot(s.x, s.y, "#ff8a3d", 3));
    state.npcs.forEach((n) => {
      const col = n.kind === "wraith" ? "#e0556b" : n.kind === "elder" ? "#ffd27a" : "#7fd08c";
      plot(n.x, n.y, col, 2.2);
    });
    state.players.forEach((p, key) => {
      if (key !== this.room.sessionId) plot(p.x, p.y, "#ffffff", 2.5);
    });
    // self
    ctx.fillStyle = "#ffb066";
    ctx.beginPath();
    ctx.arc(c, c, 3.5, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  }

  private canCraft(r: Recipe, me: Player): boolean {
    if (r.reqSkill && r.reqLevel) {
      const xp =
        r.reqSkill === Skill.Gathering
          ? me.skills.gatheringXp
          : r.reqSkill === Skill.Crafting
            ? me.skills.craftingXp
            : me.skills.combatXp;
      if (levelFromXp(xp) < r.reqLevel) return false;
    }
    for (const ing of r.inputs) {
      let have = 0;
      for (const s of me.inventory) if (s.item === ing.item) have += s.qty;
      if (have < ing.qty) return false;
    }
    return true;
  }

  private renderInventory(me: Player) {
    const inv = this.el("#inventory");
    const slots = me.inventory
      .map((s) => {
        const def = ITEMS[s.item as ItemId];
        const eat = def?.category === "food" ? "food" : "";
        const tier = s.item.startsWith("iron_") ? " iron" : "";
        // tools/weapons/armor are unique (stack 1) — hide the pointless "1"
        const showQty = (def?.stack ?? 1) > 1;
        return `<div class="slot ${eat}${tier}" data-item="${s.item}" title="${def?.name ?? s.item}${
          def?.category === "food" ? " — click to eat · shift-click to set as bait" : ""
        }"><span>${def?.icon ?? "❔"}</span>${showQty ? `<span class="qty">${s.qty}</span>` : ""}</div>`;
      })
      .join("");
    inv.innerHTML = slots || `<div style="color:var(--muted);font-size:11px;padding:6px">empty</div>`;
    inv.querySelectorAll<HTMLElement>(".slot.food").forEach((el) => {
      el.onclick = (ev) => {
        if (ev.shiftKey) {
          // bait mode: click the ground to lure an animal
          this.placingFoodItem = el.dataset.item ?? null;
          this.placingId = null;
          this.onPlacing(null);
          this.refreshPlacingUI();
          return;
        }
        this.room.send(ClientMessage.Eat, { item: el.dataset.item });
        audio.eat();
      };
    });
  }

  // ---------------------------------------------------- bug / change reports
  private bindReport() {
    const modal = this.el("#reportmodal");
    const title = this.el<HTMLInputElement>("#reporttitle");
    const body = this.el<HTMLTextAreaElement>("#reportbody");
    const send = this.el<HTMLButtonElement>("#reportsend");

    const open = () => {
      this.reportOpen = true;
      modal.classList.add("open");
      title.focus();
    };
    const close = () => {
      this.reportOpen = false;
      modal.classList.remove("open");
    };

    this.el<HTMLButtonElement>("#report").onclick = open;
    this.el<HTMLButtonElement>("#reportcancel").onclick = close;
    // click outside the card, or Esc, closes it
    modal.addEventListener("click", (e) => {
      if (e.target === modal) close();
    });
    modal.addEventListener("keydown", (e) => {
      if ((e as KeyboardEvent).key === "Escape") close();
    });

    this.root.querySelectorAll<HTMLButtonElement>(".rtype").forEach((btn) => {
      btn.onclick = () => {
        this.reportType = btn.dataset.type as TicketType;
        this.root.querySelectorAll(".rtype").forEach((b) => b.classList.remove("active"));
        btn.classList.add("active");
      };
    });

    send.onclick = async () => {
      const t = title.value.trim();
      const b = body.value.trim();
      if (t.length < 3 || b.length < 5) {
        this.toast("Add a short title and a little more detail.", "warn");
        return;
      }
      send.disabled = true;
      try {
        await api.submitReport(this.reportType, t, b, {
          url: location.href,
          screen: `${window.innerWidth}x${window.innerHeight}`,
          pos: this.me ? { x: Math.round(this.me.x), y: Math.round(this.me.y) } : null,
        });
        this.toast("Thanks — your report was sent. 🙏", "good");
        title.value = "";
        body.value = "";
        close();
      } catch (err) {
        this.toast((err as Error).message || "Could not send report.", "warn");
      } finally {
        send.disabled = false;
      }
    };
  }

  // ---------------------------------------------------- notifications
  toast(text: string, kind: "info" | "warn" | "good" = "info") {
    const t = document.createElement("div");
    t.className = `toast ${kind}`;
    t.textContent = text;
    this.el("#toasts").appendChild(t);
    setTimeout(() => t.remove(), 2800);
  }

  chat(from: string, text: string) {
    this.appendChat(`<span class="who">${from}:</span> ${this.esc(text)}`);
  }
  system(text: string) {
    this.appendChat(`<span class="sys">${this.esc(text)}</span>`);
  }
  private appendChat(html: string) {
    const log = this.el("#chatlog");
    const line = document.createElement("div");
    line.innerHTML = html;
    log.appendChild(line);
    while (log.children.length > 30) log.removeChild(log.firstChild!);
    log.scrollTop = log.scrollHeight;
  }
  private esc(s: string) {
    const d = document.createElement("div");
    d.textContent = s;
    return d.innerHTML;
  }

  quest(p: QuestPayload) {
    this.el("#quest").classList.add("show");
    this.el("#qtitle").textContent = p.title;
    this.el("#qtext").textContent = p.text;
    this.el("#qstate").textContent = p.state;
  }

  interactPrompt(text: string | null) {
    const el = this.el("#interact");
    el.classList.toggle("show", !!text);
    if (text) el.textContent = text;
  }

  setBloodMoon(on: boolean) {
    this.el("#bloodmoon").classList.toggle("show", on);
  }
}
